import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider,
  Tooltip,
  ButtonBase,
} from "@mui/material";
import {
  Menu as MenuIcon,
  DarkMode,
  LightMode,
  Logout,
  Person,
  BugReport,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "@/store";
import { logoutUser } from "@/store/authSlice";
import { useThemeMode } from "@/context/ThemeContext";
import { GoogleLoginButton } from "@/components/GoogleLoginButton";
import { SITE_SHORT_NAME } from "@/constants/app";
import { hr } from "@/constants/strings.hr";
import { useNavItems } from "@/hooks/useNavItems";

export function TopNavBar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mode, toggleTheme } = useThemeMode();
  const dispatch = useAppDispatch();
  const navItems = useNavItems();

  const { isAuthenticated, user } = useAppSelector((s) => s.auth);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleNav = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    setDrawerOpen(false);
  };

  return (
    <>
      <AppBar
        position="sticky"
        color="default"
        elevation={1}
        sx={{ backgroundColor: "background.paper" }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {isMobile && (
            <IconButton edge="start" onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              cursor: "pointer",
              mr: 3,
            }}
            onClick={() => navigate("/")}
          >
            {SITE_SHORT_NAME}
          </Typography>

          {!isMobile && (
            <Box sx={{ display: "flex", gap: 0.5, flex: 1, flexWrap: "wrap" }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  size="small"
                  onClick={() => handleNav(item.path)}
                  sx={{
                    color: isActive(item.path)
                      ? "secondary.main"
                      : "text.primary",
                    fontWeight: isActive(item.path) ? 700 : 400,
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}
          >
            <IconButton size="small" onClick={toggleTheme}>
              {mode === "dark" ? <LightMode /> : <DarkMode />}
            </IconButton>
            <Tooltip title={hr.nav.reportBug}>
              <IconButton
                size="small"
                component="a"
                href="https://bugs.pinehood.tech/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "text.secondary" }}
                aria-label={hr.nav.reportBug}
              >
                <BugReport fontSize="small" />
              </IconButton>
            </Tooltip>

            {isAuthenticated ? (
              <>
                <Button
                  size="small"
                  startIcon={<Person />}
                  onClick={() => navigate("/profil")}
                  sx={{ textTransform: "none" }}
                >
                  {!isMobile && (user?.username || hr.nav.profile)}
                </Button>
                <IconButton size="small" onClick={handleLogout}>
                  <Logout fontSize="small" />
                </IconButton>
              </>
            ) : (
              <GoogleLoginButton />
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 260, pt: 2 }}>
          <ButtonBase
            onClick={() => handleNav("/")}
            sx={{
              display: "block",
              width: "100%",
              px: 2,
              mb: 1,
              justifyContent: "flex-start",
              textAlign: "left",
              borderRadius: 1,
            }}
            aria-label={`${SITE_SHORT_NAME}: ${hr.nav.home}`}
          >
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
              }}
            >
              {SITE_SHORT_NAME}
            </Typography>
          </ButtonBase>
          <Divider />
          <List>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={isActive(item.path)}
                  onClick={() => handleNav(item.path)}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <List>
            <ListItem disablePadding>
              <ListItemButton
                component="a"
                href="https://bugs.pinehood.tech/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setDrawerOpen(false)}
              >
                <BugReport
                  sx={{ mr: 1, color: "action.active" }}
                  fontSize="small"
                />
                <ListItemText primary={hr.nav.reportBug} />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          {isAuthenticated && (
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav("/profil")}>
                  <ListItemText primary={hr.nav.profile} />
                </ListItemButton>
              </ListItem>
            </List>
          )}
        </Box>
      </Drawer>
    </>
  );
}
